<template>
  <div class="page">
    <section class="section content-container">
      <div class="container is-fluid">
        <div class="intents-charts">
          <div class="columns is-desktop">
            <div class="column is-two-thirds-desktop">
              <div class="columns">
                <div class="column is-half">
                  <div class="island">
                    <Report
                      title="Most Popular"
                      class="totals-doughnut-chart"
                      :dataEmpty="isDataEmpty(mostPopularTotals)"
                      :loadedData="loadedData"
                      :loadingError="loadingError"
                      no-labels
                    >
                      <template #chart="{ reportId }">
                        <DoughnutChart class="chart" title="Total" :data="mostPopularTotals" report-id="reportId" />
                      </template>
                    </Report>
                  </div>
                </div>
                <div class="column is-half">
                  <div class="island">
                    <Report
                      title="Lowest Confidence"
                      tooltipText="Shows the average score of each intent for the selected time period against the average score of all intents"
                      :dataEmpty="isDataEmpty(lowestConfidenceTotals)"
                      :loadedData="loadedData"
                      :loadingError="loadingError"
                      no-labels
                    >
                      <template #chart="{ reportId }">
                        <HorizontalThresholdBarChart
                          class="chart"
                          :data="lowestConfidenceTotals"
                          :threshold-data="averageConfidence"
                          :order-asc="true"
                          bar-colour="#FF6666"
                          threshold-line-colour="#E6C300"
                        />
                      </template>
                    </Report>
                  </div>
                </div>
              </div>
            </div>

            <div class="column is-one-third-desktop">
              <div class="island">
                <Report
                  title="Highest Confidence"
                  tooltipText="Shows the average score of each intent for the selected time period against the average score of all intents"
                  :dataEmpty="isDataEmpty(highestConfidenceTotals)"
                  :loadedData="loadedData"
                  :loadingError="loadingError"
                  no-labels
                >
                  <template #chart="{ reportId }">
                    <HorizontalThresholdBarChart
                      class="chart"
                      :data="highestConfidenceTotals"
                      :threshold-data="averageConfidence"
                      :order-asc="false"
                      bar-colour="#FF6666"
                      threshold-line-colour="#E6C300"
                    />
                  </template>
                </Report>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-full">
              <div class="tall island with-labels">
                <Report
                  title="Most Popular Trend"
                  :dataEmpty="isDataEmpty(mostPopularTrends)"
                  :loadedData="loadedData"
                  :loadingError="loadingError"
                  tooltipText="A trend showing the most popular intents over time"
                >
                  <template #chart="{ reportId }">
                    <LineChart class="chart" :reportId="reportId" :fill="false" :data="mostPopularTrends" />
                  </template>
                </Report>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-full">
              <div class="tall island with-labels">
                <Report
                  title="Lowest Confidence Trend"
                  :dataEmpty="isDataEmpty(lowestConfidenceTrends)"
                  :loadedData="loadedData"
                  :loadingError="loadingError"
                  tooltipText="A trend showing the average confidence score of the lowest rated intents over time"
                >
                  <template #chart="{ reportId }">
                    <LineChart
                      class="chart"
                      :reportId="reportId"
                      :fill="false"
                      :data="lowestConfidenceTrends"
                      :allowDecimalTicksY="true"
                      :labelValues="lowestLabelValues"
                    />
                  </template>
                </Report>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-full">
              <div class="tall island with-labels">
                <Report
                  title="Highest Confidence Trend"
                  :dataEmpty="isDataEmpty(highestConfidenceTrends)"
                  :loadedData="loadedData"
                  :loadingError="loadingError"
                  tooltipText="A trend showing the average confidence score of the highest rated intents over time"
                >
                  <template #chart="{ reportId }">
                    <LineChart
                      class="chart"
                      :reportId="reportId"
                      :fill="false"
                      :data="highestConfidenceTrends"
                      :allowDecimalTicksY="true"
                      :labelValues="highestLabelValues"
                    />
                  </template>
                </Report>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { Data } from '@/features/reports/models';
import { nlpReportApi } from '@/features/reports/services';
import DoughnutChart from '../components/chartjs/DoughnutChart.vue';
import HorizontalThresholdBarChart from '../components/chartjs/HorizontalThresholdBarChart.vue';
import LineChart from '../components/chartjs/LineChart.vue';
import Report from '../components/Report.vue';
import { SET_REPORT_LOADING } from '@/store/reports/mutationTypes';
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const selectedDateOption = computed(() => store.getters.selectedDateOption);

const setLoading = (loading: boolean) => store.commit(SET_REPORT_LOADING, loading);

const mostPopularTotals = ref<Data>({ series: [], scale: { labels: [], title: '' } });
const lowestConfidenceTotals = ref<Data>({ series: [], scale: { labels: [], title: '' } });
const highestConfidenceTotals = ref<Data>({ series: [], scale: { labels: [], title: '' } });

const mostPopularTrends = ref<Data>({ series: [], scale: { labels: [], title: '' } });
const lowestConfidenceTrends = ref<Data>({ series: [], scale: { labels: [], title: '' } });

const highestConfidenceTrends = ref<Data>({ series: [], scale: { labels: [], title: '' } });
const averageConfidence = ref(0);

const loadingError = ref(false);
const loadedData = ref(false);

const highestLabelValues = ref<Map<string, string>>(new Map());
const lowestLabelValues = ref<Map<string, string>>(new Map());

watch(
  selectedDateOption,
  async (filter, previous) => {
    if (filter && filter === previous) {
      return;
    }

    await renderReportData();
  },
  { immediate: true },
);

async function renderReportData() {
  if (!isReportCriterionValid()) {
    return;
  }
  loadedData.value = false;
  loadingError.value = false;
  setLoading(true);

  try {
    await loadReportData();
    loadedData.value = true;
  } catch {
    loadingError.value = true;
  } finally {
    setLoading(false);
  }
}

function isReportCriterionValid(): boolean {
  return !!selectedDateOption.value;
}

async function loadReportData(): Promise<void> {
  const filterRange = selectedDateOption.value.getDateRange();
  const responseData = await nlpReportApi.get(currentWorkspace.value.id, {
    startDate: filterRange.start,
    endDate: filterRange.end,
    grouping: selectedDateOption.value.group,
  });

  mostPopularTotals.value = responseData.mostPopularTotalsReport;
  lowestConfidenceTotals.value = responseData.lowestConfidenceTotalsReport;
  highestConfidenceTotals.value = responseData.highestConfidenceTotalsReport;
  averageConfidence.value = responseData.confidenceAverage;
  highestConfidenceTrends.value = responseData.highestConfidenceTrendReport;

  mostPopularTrends.value = responseData.mostPopularTrendReport;
  lowestConfidenceTrends.value = responseData.lowestConfidenceTrendReport;

  setAverageLabels();
}

function isDataEmpty(data: Data) {
  if (!data) {
    return false;
  }
  return data.scale.labels.length < 1 || data.series.length < 1;
}

function setAverageLabels() {
  setLabels(highestConfidenceTotals.value, highestLabelValues.value);
  setLabels(lowestConfidenceTotals.value, lowestLabelValues.value);
}

function setLabels(data: Data, labels: Map<string, string>) {
  if (!isDataEmpty(data)) {
    data.series[0].data.forEach((average, index) => {
      const roundedAverage = Math.round(average * 1000) / 1000;
      labels.set(data.scale.labels[index], roundedAverage.toString());
    });
  }
}
</script>

<style lang="scss">
@media (max-width: 1450px) {
  .intents-charts {
    .legend-container {
      display: none !important;
    }
    .doughnut-chart-container {
      margin: auto;
    }
  }
}
</style>

<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;

// removes bottom padding/margin for a set
// of columns nested within a set of columns
// as this causes double spacing at lower breakpoints
.columns > .columns:not(:last-of-type) {
  padding-bottom: 0;
  margin-bottom: 0;
}

.intents-charts {
  display: flex;
  flex-direction: column;

  .container.is-fluid {
    margin-left: 0;
    margin-right: 1px;
  }
}

.report-container:not(:last-child) {
  margin-bottom: 1.5em;
}

.island {
  flex: 1;
  min-height: 370px;
  height: 100%;

  &.tall {
    min-height: 560px;
    max-height: 560px;
  }

  &.is-full {
    // for height responsiveness
    display: flex;
    flex-direction: column;
  }
}
</style>
